import { Component, Input } from '@angular/core'
import { ConfirmDialogService, Toaster } from '@tokeet-frontend/tv3-platform'
import { BdcRtbRequest, Inquiry, isBdcRtbRequest, isPendingBdcRtbRequest } from '@tv3/store/inquiry/inquiry.model'
import { finalize } from 'rxjs/operators'
import moment from 'moment'
import {
  BdcRtbGuestSignal,
  bdcRtbDisclaimer,
  bdcRtbRejectionReasons,
  bdcRtbStatusLabels,
  getBdcRtbGuestSignals,
} from '../bdc-request-to-book.model'
import { BdcRequestToBookService } from '../bdc-request-to-book.service'
import { DeclineBdcRequestDialogService } from '../decline-bdc-request-dialog/decline-bdc-request-dialog.service'

// TV4-6322: a Booking.com Request to Book request, with Approve / Decline while it waits for the host.
@Component({
  standalone: false,
  selector: 'app-bdc-rtb-request-panel',
  templateUrl: './bdc-rtb-request-panel.component.html',
  styleUrls: ['./bdc-rtb-request-panel.component.scss'],
})
export class BdcRtbRequestPanelComponent {
  private _inquiry: Inquiry
  @Input() set inquiry(inquiry: Inquiry) {
    this._inquiry = inquiry
    this.request = isBdcRtbRequest(inquiry) ? inquiry.attributes.bdc_rtb : null
    this.isPending = isPendingBdcRtbRequest(inquiry)
    this.signals = getBdcRtbGuestSignals(this.request?.guest_info)
  }
  get inquiry() {
    return this._inquiry
  }

  // A narrower layout for the inbox side panel.
  @Input() compact = false

  request: BdcRtbRequest
  isPending = false
  signals: BdcRtbGuestSignal[] = []
  isLoading = false

  disclaimer = bdcRtbDisclaimer

  constructor(
    private rtbService: BdcRequestToBookService,
    private declineDialog: DeclineBdcRequestDialogService,
    private confirmDialog: ConfirmDialogService,
    private toaster: Toaster
  ) {}

  get statusLabel() {
    return bdcRtbStatusLabels[this.request?.request_status] || this.request?.request_status
  }

  get rejectionReasonLabel() {
    return bdcRtbRejectionReasons.find((r) => r.id === this.request?.rejection_reason)?.label
  }

  // RtB 2.0: the guest card is pre-authorised and Booking.com confirms at approval.
  get isRtb() {
    return this.request?.request_type === 'RTB'
  }

  // The backend holds the dates only while an RtB 2.0 request is open; once it is booked the
  // reservation keeps them closed, and every other outcome releases them.
  get isHeld() {
    return this.isRtb && ['CREATED', 'APPROVED'].includes(this.request?.request_status)
  }

  get isOpen() {
    return ['CREATED', 'APPROVED'].includes(this.request?.request_status)
  }

  get expiresSoon() {
    return !!this.request?.expires_at && this.request.expires_at - moment().unix() < 6 * 3600
  }

  get isExpiredLocally() {
    return !!this.request?.expires_at && this.request.expires_at < moment().unix()
  }

  onApprove() {
    const body = this.isRtb
      ? $localize`Booking.com confirms the reservation as soon as you approve, and the guest's card is charged according to your policies.`
      : $localize`The guest then has 24 hours to book. The price can change until the guest books.`

    this.confirmDialog
      .confirm({
        title: $localize`Approve this booking request?`,
        body: `${body}<br><br>${this.disclaimer}`,
        confirmText: $localize`Approve`,
        cancelText: $localize`Go back`,
      })
      .subscribe(() => {
        this.isLoading = true
        this.rtbService
          .decide(this.inquiry, { status: 'APPROVED' })
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe(
            () => this.toaster.success($localize`The booking request was approved.`),
            (error) => {
              this.toaster.error($localize`Unable to approve the booking request`, '', error)
              this.rtbService.refresh(this.inquiry)
            }
          )
      })
  }

  onDecline() {
    this.declineDialog.open(this.inquiry)
  }
}
